import { Avatar, Badge, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import type { TeamMember } from '../../lib/api';
import { PUNCH_LABEL, STATE_COLOR, STATE_LABEL, formatMinutes, formatSince } from '../../lib/attendance';

function minutesSince(iso: string | null, now: number): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
}

/**
 * „Team jetzt" — eine Zeile je Kollege: arbeitet / Pause / weg und seit wann. Die Zusatzangaben der
 * Leitung (`details`) liefert der Server nur an ADMIN und TEAMLEAD; fehlen sie, zeigt die Zeile nur
 * den Status.
 */
export function TeamNowList({ members, compact = false }: { members: TeamMember[]; compact?: boolean }) {
  const now = Date.now();
  return (
    <Stack gap={compact ? 4 : 'xs'}>
      {members.map((m) => {
        const since = formatSince(m.since);
        const d = m.details;
        return (
          <Paper key={m.userId} withBorder={!compact} p={compact ? 4 : 'sm'} radius="md">
            <Group justify="space-between" wrap="nowrap" gap="xs">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Avatar size={compact ? 'sm' : 'md'} radius="xl" color={m.color ?? 'gray'} variant="filled" autoContrast>
                  {m.name.charAt(0).toUpperCase()}
                </Avatar>
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {m.name}
                  </Text>
                  {since && (
                    <Text size="xs" c="dimmed">
                      seit {since}
                    </Text>
                  )}
                </div>
              </Group>
              <Group gap={6} wrap="nowrap">
                {d && (
                  <Tooltip label={d.online ? 'Online (Aktivität in den letzten 5 Min.)' : 'Nicht online'} withArrow>
                    <Badge size="xs" variant="dot" color={d.online ? 'teal' : 'gray'}>
                      {d.online ? 'online' : 'offline'}
                    </Badge>
                  </Tooltip>
                )}
                <Badge color={STATE_COLOR[m.state]} variant={m.state === 'OFF' ? 'outline' : 'filled'}>
                  {STATE_LABEL[m.state]}
                </Badge>
              </Group>
            </Group>

            {d && !compact && (
              <Stack gap={2} mt="xs">
                <Text size="xs" c="dimmed">
                  {d.loggedInSince
                    ? `Eingeloggt seit ${formatSince(d.loggedInSince)} (${formatMinutes(d.loggedInMinutes ?? 0)})`
                    : 'Nicht eingeloggt'}
                  {d.lastActivity && ` · letzte Aktivität ${formatSince(d.lastActivity)}`}
                  {d.lastActivity && minutesSince(d.lastActivity, now) !== null && ` (vor ${minutesSince(d.lastActivity, now)} Min.)`}
                </Text>
                {d.activeSessions > 0 && (
                  <Text size="xs" c="dimmed">
                    {d.activeSessions} {d.activeSessions === 1 ? 'Sitzung' : 'Sitzungen'}: {d.devices.join(', ')}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  Heute {formatMinutes(d.workMinutesToday)} Arbeit · {formatMinutes(d.breakMinutesToday)} Pause
                </Text>
                {d.punchesToday.length > 0 && (
                  <Text size="xs" c="dimmed">
                    {d.punchesToday.map((p) => `${formatSince(p.at)} ${PUNCH_LABEL[p.type]}${p.source === 'ON_CALL' ? ' (RB)' : ''}`).join(' · ')}
                  </Text>
                )}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}
